import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Code as CodeIcon
} from '@mui/icons-material';

// Create Rule Modal
export const CreateRuleModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  form, 
  setForm, 
  loading, 
  projects = [],
  stageOptions = ['event_store'],
  getStageDescription = () => ''
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 1, 
            bgcolor: 'success.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center'
          }}>
            <AddIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="600">
              Create Rule
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add a new rule engine definition
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 3,
          maxWidth: '600px'
        }}>
          {/* Project Selection */}
          <FormControl fullWidth required error={!form.project_id}>
            <InputLabel>Project *</InputLabel>
            <Select
              value={form.project_id}
              onChange={(e) => setForm(prev => ({ ...prev, project_id: e.target.value }))}
              label="Project *"
              startAdornment={
                <Box sx={{ mr: 1, color: 'text.secondary' }}>
                  🏢
                </Box>
              }
              sx={{ minHeight: '56px' }}
            >
              {projects.length === 0 ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading projects...</Typography>
                  </Box>
                </MenuItem>
              ) : (
                projects.map(project => (
                  <MenuItem key={project.unique_id} value={project.unique_id}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {project.organization_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {project.unique_id}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
            {!form.project_id && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                Please select a project
              </Typography>
            )}
          </FormControl>

          {/* Rule Name */}
          <TextField
            fullWidth
            label="Rule Name *"
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            required
            error={!form.name}
            helperText={!form.name ? "Rule name is required" : "Enter a descriptive name for this rule"}
            startAdornment={
              <Box sx={{ mr: 1, color: 'text.secondary' }}>
                📝
              </Box>
            }
            sx={{ minHeight: '80px' }}
          />

          {/* Stage Selection */}
          <FormControl fullWidth required error={!form.stage}>
            <InputLabel>Stage *</InputLabel>
            <Select
              value={form.stage}
              onChange={(e) => setForm(prev => ({ ...prev, stage: e.target.value }))}
              label="Stage *"
              startAdornment={
                <Box sx={{ mr: 1, color: 'text.secondary' }}>
                  ⚙️
                </Box>
              }
              sx={{ minHeight: '56px' }}
            >
              {stageOptions.map(stage => (
                <MenuItem key={stage} value={stage}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {stage.replace('_', ' ').toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getStageDescription(stage)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {!form.stage && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                Please select a stage
              </Typography>
            )}
          </FormControl>

          {/* Active Status */}
          <Box sx={{ 
            p: 2, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            minHeight: '80px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Active Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {form.active ? 'Rule will be active immediately' : 'Rule will be inactive'}
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Rule Definition */}
          <TextField
            fullWidth
            label="Rule Definition *"
            value={form.rule}
            onChange={(e) => setForm(prev => ({ ...prev, rule: e.target.value }))}
            multiline
            rows={12}
            required
            error={!form.rule}
            helperText={
              !form.rule 
                ? "Rule definition is required" 
                : "Enter the rule logic in JSON format. Use 'true'/'false' (not True/False) and ensure all strings are quoted. Example: {\"condition\": \"value\", \"action\": true}"
            }
            sx={{
              minHeight: '320px',
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }
            }}
            startAdornment={
              <Box sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 2 }}>
                🔧
              </Box>
            }
          />

          {/* JSON Validation */}
          {form.rule && (
            <Box sx={{ 
              p: 2, 
              bgcolor: (() => {
                try {
                  JSON.parse(form.rule);
                  return 'success.light';
                } catch {
                  return 'error.light';
                }
              })(),
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minHeight: '48px'
            }}>
              {(() => {
                try {
                  JSON.parse(form.rule);
                  return <CheckCircleIcon color="success" />;
                } catch {
                  return <CancelIcon color="error" />;
                }
              })()}
              <Typography variant="body2" color={(() => {
                try {
                  JSON.parse(form.rule);
                  return 'success.dark';
                } catch {
                  return 'error.dark';
                }
              })()}>
                {(() => {
                  try {
                    JSON.parse(form.rule);
                    return '✓ Valid JSON format';
                  } catch {
                    return '✗ Invalid JSON format';
                  }
                })()}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        p: 2,
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={loading || !form.project_id || !form.name || !form.stage || !form.rule}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          size="large"
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Creating...' : 'Create Rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Rule Modal
export const EditRuleModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  form, 
  setForm, 
  loading, 
  stageOptions = ['event_store'],
  getStageDescription = () => ''
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 1, 
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center'
          }}>
            <EditIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="600">
              Edit Rule
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update the rule engine definition
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 6 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 3,
          maxWidth: '600px'
        }}>
          {/* Rule Name */}
          <TextField
            fullWidth
            label="Rule Name *"
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            required
            error={!form.name}
            helperText={!form.name ? "Rule name is required" : "Enter a descriptive name for this rule"}
            startAdornment={
              <Box sx={{ mr: 1, color: 'text.secondary' }}>
                📝
              </Box>
            }
            sx={{ minHeight: '80px' }}
          />

          {/* Stage Selection */}
          <FormControl fullWidth required error={!form.stage}>
            <InputLabel>Stage *</InputLabel>
            <Select
              value={form.stage}
              onChange={(e) => setForm(prev => ({ ...prev, stage: e.target.value }))}
              label="Stage *"
              startAdornment={
                <Box sx={{ mr: 1, color: 'text.secondary' }}>
                  ⚙️
                </Box>
              }
              sx={{ minHeight: '56px' }}
            >
              {stageOptions.map(stage => (
                <MenuItem key={stage} value={stage}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {stage.replace('_', ' ').toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getStageDescription(stage)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {!form.stage && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                Please select a stage
              </Typography>
            )}
          </FormControl>

          {/* Active Status */}
          <Box sx={{ 
            p: 2, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            minHeight: '80px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Active Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {form.active ? 'Rule will be active immediately' : 'Rule will be inactive'}
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Rule Definition */}
          <TextField
            fullWidth
            label="Rule Definition *"
            value={form.rule}
            onChange={(e) => setForm(prev => ({ ...prev, rule: e.target.value }))}
            multiline
            rows={12}
            required
            error={!form.rule}
            helperText={
              !form.rule 
                ? "Rule definition is required" 
                : "Enter the rule logic in JSON format. Use 'true'/'false' (not True/False) and ensure all strings are quoted. Example: {\"condition\": \"value\", \"action\": true}"
            }
            sx={{
              minHeight: '320px',
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }
            }}
            startAdornment={
              <Box sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 2 }}>
                🔧
              </Box>
            }
          />

          {/* JSON Validation */}
          {form.rule && (
            <Box sx={{ 
              p: 2, 
              bgcolor: (() => {
                try {
                  JSON.parse(form.rule);
                  return 'success.light';
                } catch {
                  return 'error.light';
                }
              })(),
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minHeight: '48px'
            }}>
              {(() => {
                try {
                  JSON.parse(form.rule);
                  return <CheckCircleIcon color="success" />;
                } catch {
                  return <CancelIcon color="error" />;
                }
              })()}
              <Typography variant="body2" color={(() => {
                try {
                  JSON.parse(form.rule);
                  return 'success.dark';
                } catch {
                  return 'error.dark';
                }
              })()}>
                {(() => {
                  try {
                    JSON.parse(form.rule);
                    return '✓ Valid JSON format';
                  } catch {
                    return '✗ Invalid JSON format';
                  }
                })()}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        p: 2,
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={loading || !form.name || !form.stage || !form.rule}
          startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
          size="large"
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Updating...' : 'Update Rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// View Rule Modal
export const ViewRuleModal = ({ 
  open, 
  onClose, 
  onEdit, 
  rule, 
  getProjectName = () => '',
  getStageColor = () => 'default',
  getStatusColor = () => 'default',
  formatDate = () => ''
}) => {
  if (!rule) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 1, 
            bgcolor: 'info.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center'
          }}>
            <ViewIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="600">
              Rule Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View complete information about this rule engine definition
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Header Section */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: 3,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CodeIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="600" gutterBottom>
                {rule.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={rule.stage.replace('_', ' ').toUpperCase()}
                  color={getStageColor(rule.stage)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={rule.active ? 'Active' : 'Inactive'}
                  color={getStatusColor(rule.active)}
                  icon={rule.active ? <CheckCircleIcon /> : <CancelIcon />}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  Created {formatDate(rule.create_date)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Details Grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ height: '100%', p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ 
                    p: 0.5, 
                    borderRadius: 0.5, 
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    🏢
                  </Box>
                  <Typography variant="subtitle1" fontWeight="600">
                    Project Information
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Project Name
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                  {getProjectName(rule.project_id)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Project ID
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontFamily: 'monospace', 
                  bgcolor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                  fontSize: '0.875rem'
                }}>
                  {rule.project_id || 'No Project ID'}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ height: '100%', p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ 
                    p: 0.5, 
                    borderRadius: 0.5, 
                    bgcolor: 'info.light',
                    color: 'info.contrastText',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    📋
                  </Box>
                  <Typography variant="subtitle1" fontWeight="600">
                    Rule Information
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Rule ID
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontFamily: 'monospace', 
                  bgcolor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  mb: 2
                }}>
                  {rule.unique_id}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Stage
                </Typography>
                <Chip
                  label={rule.stage.replace('_', ' ').toUpperCase()}
                  color={getStageColor(rule.stage)}
                  size="small"
                  variant="outlined"
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Rule Definition Section */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Box sx={{ 
                p: 0.5, 
                borderRadius: 0.5, 
                bgcolor: 'success.light',
                color: 'success.contrastText',
                display: 'flex',
                alignItems: 'center'
              }}>
                ⚙️
              </Box>
              <Typography variant="subtitle1" fontWeight="600">
                Rule Definition
              </Typography>
            </Box>
            <Paper sx={{ 
              p: 3, 
              bgcolor: 'grey.50', 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              maxHeight: '400px',
              overflow: 'auto'
            }}>
              <pre style={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}>
                {(() => {
                  try {
                    // Check if rule is already an object or needs parsing
                    const ruleData = typeof rule.rule === 'string' 
                      ? JSON.parse(rule.rule) 
                      : rule.rule;
                    return JSON.stringify(ruleData, null, 2);
                  } catch (error) {
                    // If parsing fails, display as is
                    return typeof rule.rule === 'string' 
                      ? rule.rule 
                      : JSON.stringify(rule.rule, null, 2);
                  }
                })()}
              </pre>
            </Paper>
          </Paper>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        p: 2,
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          size="large"
        >
          Close
        </Button>
        <Button
          onClick={onEdit}
          variant="contained"
          startIcon={<EditIcon />}
          size="large"
          sx={{ minWidth: 140 }}
        >
          Edit Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete Confirmation Modal
export const DeleteRuleModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  rule, 
  loading 
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Delete Rule
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to delete the rule "{rule?.name}"? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {loading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 